import { AxiosPromise, AxiosResponse } from 'axios';
import {apiService, manageFilters} from '@app/services';
import {IActiveFilter, IVaultPasswordPolicy, Order} from '@app/models';
import {infiniteScrollItemsFetchLimit} from '@app/helpers';

class PasswordManagerPolicyService {
    readonly apiBase: string = '/api/directory/passwordserverpolicies';
    readonly apiBase2: string = '/api/v1/pm/password-policies';

    getPolicies(orderBy: string, asc: string, top: number, skip: number, isReset: boolean,
        handleError: Function, handleSuccess: Function, filterText: boolean, filters?: IActiveFilter[]): AxiosPromise {

        const apiUrl = this.apiBase + '/Policies';

        return apiService.get(apiUrl, {
            params: {
                $orderBy: orderBy + ' ' + asc,
                $top: top,
                $skip: skip,
                $count: isReset,
                $filter: manageFilters(filters),
            },
            onError: handleError, onSuccess: handleSuccess
        })
            .catch();
    }

    async getAllPolicies(asc: string, skip: number = 0, values: any[] = []): Promise<any[]> {
        const resp: AxiosResponse = await this.getPolicies(
            'DisplayName',
            Order.asc,
            100,
            skip,
            true,
            () => {},
            () => {},
            true,
            undefined
        );

        skip += resp.data.value.length;
        values = [...values, ...resp.data.value];

        if (skip < resp.data['@odata.count']) {
            return this.getAllPolicies(asc, skip, values);
        } else {
            return values;
        }
    }

    getPolicy(id: string, handleError: Function, handleSuccess: Function) {
        return apiService.get(`${this.apiBase}(${id})`, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    deletePolicy(jsonObject: any, handleError: Function, handleSuccess: Function) {
        return apiService.post(`${this.apiBase}/DeletePolicies`, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    addPolicy(jsonObject: any, handleError: Function, handleSuccess: Function) {
        return apiService.post(`${this.apiBase}`, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    editPolicy(jsonObject: any, id: string, handleError: Function, handleSuccess: Function) {
        return apiService.put(`${this.apiBase}(${id})`, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    getPolicies2(
        orderBy: string,
        asc: string,
        take: number,
        skip: number,
        handleError: Function,
        handleSuccess: Function,
        search?: string
    ): AxiosPromise {
        const newAsc = asc === Order.asc ? 'a' : 'd'; // TODO: Have to be changed on the back-end

        if (search !== '') {
            return apiService
                .get(this.apiBase2, {
                    params: {
                        orderBy: orderBy + ' ' + newAsc,
                        take: take,
                        skip: skip,
                        filter: search
                    },
                    onError: handleError,
                    onSuccess: handleSuccess
                })
                .catch();
        } else {
            return apiService
                .get(this.apiBase2, {
                    params: {
                        orderBy: orderBy + ' ' + newAsc,
                        take: take,
                        skip: skip
                    },
                    onError: handleError,
                    onSuccess: handleSuccess
                })
                .catch();
        }
    }

    addPolicy2(jsonObject: any, handleError: Function, handleSuccess: Function) {
        return apiService.post(`${this.apiBase2}`, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    editPolicy2(jsonObject: any, id: string, handleError: Function, handleSuccess: Function) {
        return apiService.put(`${this.apiBase2}/${id}`, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    deletePolicy2(jsonObject: any, handleError: Function, handleSuccess: Function) {
        return apiService
            .delete(`${this.apiBase2}`, {
                data: jsonObject,
                onSuccess: handleSuccess,
                onError: handleError
            })
            .catch();
    }

    getPolicy2(id: string, handleError: Function, handleSuccess: Function) {
        return apiService.get(`${this.apiBase2}/${id}`, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    getAllPolicies2() {
        let values: IVaultPasswordPolicy[] = [];
        let count = 0;

        const getAllPoliciesInfo = (): Promise<IVaultPasswordPolicy[]> => {
            return this.getPolicies2(
                'displayName',
                Order.asc,
                infiniteScrollItemsFetchLimit,
                0,
                () => {},
                () => {}
            ).then((resp: AxiosResponse) => {
                count += resp.data.items.length;
                values = [...values, ...resp.data.items];

                if (count < resp.data.count) {
                    return getAllPoliciesInfo();
                } else {
                    return values;
                }
            });
        };

        return getAllPoliciesInfo();
    }

    addVault(jsonObject: any, handleError: Function, handleSuccess: Function) {
        return apiService.post(`${this.apiBase}`, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }
}

export const passwordManagerPolicyService = new PasswordManagerPolicyService();
